<script setup lang="ts">
import type { Question } from '../../../question/models/Question'

const props = defineProps<{
  question: Pick<Question<'maxDiffSet'>, 'bestLabel' | 'worstLabel' | 'fields'>
  bestFieldId: string | undefined
  worstFieldId: string | undefined
}>()

const emit = defineEmits<{
  selectField: [fieldId: string, type: 'best' | 'worst']
}>()
</script>
<template>
  <div class="max-diff-set-question">
    <div class="max-diff-set-question__labels">
      <div class="max-diff-set-question__label">
        {{ props.question.bestLabel }}
      </div>
      <div class="max-diff-set-question__label">
        {{ props.question.worstLabel }}
      </div>
    </div>
    <div class="max-diff-set-question__fields">
      <div
        v-for="field in props.question.fields.filter((f) => f.type === 'maxDiffSetChoice')"
        :key="field.id"
        class="max-diff-set-question__field"
      >
        <input
          name="best"
          :checked="field.id === props.bestFieldId"
          :disabled="field.id === props.worstFieldId"
          class="max-diff-set-question__button"
          type="radio"
          :onClick="() => emit('selectField', field.id, 'best')"
          :data-test-id="`MaxDiffSetQuestion_Best_Field-${field.id}`"
        />
        <div class="max-diff-set-question__field-title">
          {{ field.title }}
        </div>
        <input
          name="worst"
          :checked="field.id === props.worstFieldId"
          :disabled="field.id === props.bestFieldId"
          class="max-diff-set-question__button"
          type="radio"
          :onClick="() => emit('selectField', field.id, 'worst')"
          :data-test-id="`MaxDiffSetQuestion_Worst_Field-${field.id}`"
        />
      </div>
    </div>
  </div>
</template>
<style lang="postcss">
.max-diff-set-question {
  display: grid;
  overflow: auto;
  height: 100%;
  padding: var(--attest-spacing-4);
  gap: var(--attest-spacing-4);
  grid-template-rows: min-content auto;

  &__labels {
    --gap: var(--attest-spacing-2);

    display: grid;
    justify-content: space-between;
    gap: var(--gap);
    grid-template-columns: repeat(2, calc(50% - var(--gap) * 0.5));
  }

  &__label {
    color: #036;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;

    &:last-of-type {
      text-align: right;
    }
  }

  --border-color: #c0c2c2;

  &__field {
    display: grid;
    align-items: center;
    background-color: #fff;
    grid-template-columns: 44px auto 44px;
    text-align: center;
  }

  &__field:last-of-type {
    border-bottom: 1px solid var(--border-color);
  }

  &__field-title {
    padding: var(--attest-spacing-3) var(--attest-spacing-2);
    border-top: 1px solid var(--border-color);
  }

  &__button {
    position: relative;
    display: grid;
    height: 100%;
    padding: var(--attest-spacing-2);
    border-top: 1px solid var(--border-color);
    border-right: 1px solid var(--border-color);
    border-left: 1px solid var(--border-color);
    margin: 0;
    appearance: none;
    place-items: center;

    &:disabled {
      background-color: #f5f5f5;
    }

    &:not(:disabled, :checked):hover::before {
      border-color: #6e6f6f;
    }

    &::before {
      display: block;
      width: 16px;
      height: 16px;
      border: 2px solid var(--border-color);
      border-radius: 50%;
      content: '';
    }

    &:checked::before {
      border-color: #00829b;
    }

    &:checked::after {
      position: absolute;
      top: 50%;
      left: 50%;
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background-color: #00829b;
      content: '';
      transform: translate(-50%, -50%);
    }
  }
}
</style>
